import React, { useState, useEffect } from 'react';
import Modal from '../Modal';
import { Exportateur } from '../../types/weighing';

interface EditExporterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (exporter: Partial<Exportateur>) => void;
  exporter: Exportateur;
}

const EditExporterModal: React.FC<EditExporterModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  exporter 
}) => {
  const [formData, setFormData] = useState<Partial<Exportateur>>({
    nom: '',
    type: '',
    adresse: '',
    telephone: '',
    email: '',
    statut: 'actif'
  });

  const [errors, setErrors] = useState<Partial<Exportateur>>({});

  useEffect(() => {
    if (exporter) {
      setFormData({
        nom: exporter.nom || '',
        type: exporter.type || '',
        adresse: exporter.adresse || '',
        telephone: exporter.telephone || '',
        email: exporter.email || '',
        statut: exporter.statut || 'actif'
      });
    }
  }, [exporter]);

  const handleInputChange = (field: keyof Exportateur, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Exportateur> = {};

    if (!formData.nom?.trim()) {
      newErrors.nom = 'Le nom est requis';
    }
   
    // Les champs adresse, telephone et email sont maintenant optionnels

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave(formData);
      onClose();
      setErrors({});
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Modifier l'exportateur" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Nom et Type */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.nom || ''}
              onChange={(e) => handleInputChange('nom', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.nom ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Nom de l'exportateur"
            />
            {errors.nom && (
              <p className="mt-1 text-sm text-red-600">{errors.nom}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type
            </label>
            <select
              value={formData.type || ''}
              onChange={(e) => handleInputChange('type', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.type ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Sélectionner un type</option>
              <option value="entreprise-privee">Entreprise privée</option>
              <option value="cooperative">Coopérative</option>
              <option value="negociant">Négociant</option>
              <option value="grossiste">Grossiste</option>
              <option value="importateur">Importateur</option>
              <option value="autre">Autre</option>
            </select>
            {errors.type && (
              <p className="mt-1 text-sm text-red-600">{errors.type}</p>
            )}
          </div>
        </div>


        {/* Adresse */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Adresse
          </label>
          <textarea
            value={formData.adresse || ''}
            onChange={(e) => handleInputChange('adresse', e.target.value)}
            rows={3}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
              errors.adresse ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Adresse complète de l'exportateur"
          />
          {errors.adresse && (
            <p className="mt-1 text-sm text-red-600">{errors.adresse}</p>
          )}
        </div>

        {/* Téléphone et Email */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Téléphone
            </label>
            <input
              type="tel"
              value={formData.telephone || ''}
              onChange={(e) => handleInputChange('telephone', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.telephone ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="+221 77 123 45 67"
            />
            {errors.telephone && (
              <p className="mt-1 text-sm text-red-600">{errors.telephone}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={formData.email || ''}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="exportateur@email.com"
            />
          </div>
        </div>

        {/* Statut */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Statut
          </label>
          <select
            value={formData.statut || 'actif'}
            onChange={(e) => handleInputChange('statut', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="actif">Actif</option>
            <option value="inactif">Inactif</option>
          </select>
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            Modifier l'exportateur
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default EditExporterModal;
